import * as React from 'react';
import { cn, evaluateBooleanOrPredicate } from '../../../../utils';
import type { ComplexTreeRenderItemOptions, ComplexTreeRenderItemProps } from '../types';
import { ComplexTreeItemChevron } from './item-chevron';
import { ComplexTreeMoreOptions } from './item-more-options';
import { ComplexTreeItemTitle } from './item-title';

const DEFAULT_RENDER_DEPTH_OFFSET = 15;

export function createComplexTreeRenderItem({
	renderDepthOffset = DEFAULT_RENDER_DEPTH_OFFSET,
	doesItemMatchSearch,
	searchTerm,
	maxDepth,
	depthLimitedText,
	maxChildrenPerNode,
	truncatedText,
	moreOptions = [],
	ariaLabels,
}: ComplexTreeRenderItemOptions = {}): React.FC<ComplexTreeRenderItemProps> {
	return function ComplexTreeRenderItem({ item, depth, children, context }: ComplexTreeRenderItemProps) {
		const label = item?.data?.label || '';
		const subtitle = item?.data?.subtitle;
		const fullChildCount = Array.isArray(item.data.children) ? item.data.children.length : 0;
		const computedIsTruncated = maxChildrenPerNode !== undefined && fullChildCount > maxChildrenPerNode;
		const truncatedCount = computedIsTruncated ? Math.max(0, fullChildCount - (maxChildrenPerNode ?? 0)) : 0;
		const isDepthLimited = maxDepth !== undefined && maxDepth !== null && depth >= maxDepth;
		const hasChildren = Boolean(item.children?.length);
		const matchesSearch = Boolean(doesItemMatchSearch && searchTerm && doesItemMatchSearch(item, searchTerm));
		const hideMoreOptions = evaluateBooleanOrPredicate(item.data?.hideMoreOptions, item.data, false);

		const depthPaddingStyle = {
			paddingLeft: `${depth * renderDepthOffset}px`,
		};

		const onArrowButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
			e.preventDefault();
			e.stopPropagation();
			context.toggleExpandedState();
		};

		const expandButtonAriaLabel = ariaLabels?.expandButton?.(label, Boolean(context.isExpanded));
		const selectButtonAriaLabel = ariaLabels?.selectButton?.(label, Boolean(context.isSelected));
		const moreOptionsButtonAriaLabel = ariaLabels?.moreOptionsButton?.(label);

		return (
			<li
				className={'w-full py-[0.3rem] font-sans text-primary'}
				data-testid="complex-tree-item"
				role="treeitem"
				aria-label={ariaLabels?.treeItem?.(label)}
				aria-expanded={hasChildren ? context.isExpanded : undefined}
				aria-selected={context.isSelected}
			>
				<div style={depthPaddingStyle}>
					<div
						className={cn(
							'group flex w-full items-center rounded-[0.4rem] hover:bg-bgSecondary',
							context.isSelected && 'bg-bgSecondary'
						)}
						data-testid="complex-tree-item-container"
					>
					<button
						className={cn(
							'h-[4.8rem] w-[3.6rem] shrink-0 justify-center rounded-[0.4rem]',
							'hover:bg-bgPrimary',
							context.isSelected && 'shadow-[inset_0.25rem_0_0_var(--color-primary)]',
							!hasChildren && 'pointer-events-none'
						)}
						{...(context.arrowProps as React.ButtonHTMLAttributes<HTMLButtonElement>)}
						onClick={onArrowButtonClick}
						aria-hidden={!hasChildren}
						aria-expanded={hasChildren ? context.isExpanded : undefined}
						aria-label={hasChildren ? expandButtonAriaLabel : undefined}
						data-testid="complex-tree-item-chevron-button"
					>
						<ComplexTreeItemChevron item={item} isExpanded={Boolean(context.isExpanded)} />
					</button>
						<button
							{...(context.interactiveElementProps as React.ButtonHTMLAttributes<HTMLButtonElement>)}
							className={'h-[4.8rem] w-full min-w-0 shrink rounded-[0.4rem] text-left hover:bg-bgPrimary'}
							data-testid="complex-tree-item-title-button"
							aria-label={selectButtonAriaLabel}
						>
							<ComplexTreeItemTitle
								title={label}
								subtitle={subtitle}
								isTruncated={computedIsTruncated}
								truncatedCount={truncatedCount}
								truncatedText={truncatedText}
								isDepthLimited={isDepthLimited}
								depthLimitedText={depthLimitedText}
								matchesSearch={matchesSearch}
							/>
						</button>
						{!hideMoreOptions && (
							<ComplexTreeMoreOptions
								moreOptions={moreOptions}
								item={item}
								className={cn(
									'transition-opacity duration-100',
									context.isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
								)}
								ariaLabel={moreOptionsButtonAriaLabel}
							/>
						)}
					</div>
				</div>
				{children}
			</li>
		);
	};
}
